<template>
  <ts-modal
    ref="modal"
    :modal-title="modalTitle"
    modal-class="m-modal modal-confirmation"
    @modal-shown="onModalShown"
    @modal-hidden="onModalHidden"
  >
    <template #modal-content>
      <alert
        v-if="errorVisible"
        :message="errorMessage"
        :dismissable="true"
        @dismiss="clearError"
      />
      <template v-if="isLoadingConfirmation">
        <loading-indicator
          :use-flexbox="true"
        />
      </template>
      <template v-else-if="isPerformingAction && confirmation">
        <loading-indicator
          :use-flexbox="true"
          :message="confirmation.actionMessage"
          :vertical="true"
        />
      </template>
      <alert
        v-else-if="confirmationResult"
        :dismissable="false"
        :message="confirmationResult.message"
        :type="confirmationResult.isSuccess ? 'success' : 'danger'"
      >
        <template v-if="confirmationResult.details?.length">
          <ul>
            <li
              v-for="(detail, detailIdx) of confirmationResult.details"
              :key="`detail-${detailIdx}`"
            >
              {{ detail }}
            </li>
          </ul>
        </template>
      </alert>
      <template v-else-if="confirmation">
        <div
          v-if="confirmation.displayMode == 'alert'"
          :class="['alert', confirmation.alertType]"
        >
          {{ confirmation.message }}
        </div>
        <template v-else>
          {{ confirmation.message }}
        </template>
      </template>
      <slot
        name="confirmation-content"
        :confirmation-result="confirmationResult"
      />
    </template>
    <template #modal-footer>
      <button
        v-if="!isLoadingConfirmation"
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        {{ cancelTitle }}
      </button>
      <template v-if="confirmation && !confirmationResult">
        <button
          :class="['btn btn-sm', confirmation.actionButtonType || 'btn-primary']"
          @click="onConfirmAction"
        >
          {{ confirmation.actionTitle }}
        </button>
      </template>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import {computed, ref, useTemplateRef} from 'vue'
import type { Confirmation, ConfirmationResult } from '../ospe-types'
import axios from 'axios'
import Rails from '@rails/ujs'
import Jsona from 'jsona'
import { isNonEmptyString } from '../js-utils'
import { getAxiosError } from '../axios-utils'

import Alert from './alert.vue'
import LoadingIndicator from './loading-indicator.vue'
import TsModal from "@/js/vue-components/ts-modal.vue";

const emit = defineEmits<{
  (e: 'modal-hidden'): void
  (e: 'modal-shown'): void
  (e: 'confirmed'): void
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>("modal")

const modalTitle = ref<string>('Confirmation')
const confirmation = ref<Confirmation | null>(null)
const confirmationResult = ref<ConfirmationResult | null>(null)
const isLoadingConfirmation = ref<boolean>(false)
const isPerformingAction = ref<boolean>(false)
const errorMessage = ref<string>('')
const errorDetails = ref<string[] | null>(null)
const cancelTitle = ref<'Close' | 'Cancel'>('Cancel')

const errorVisible = computed((): boolean => {
  return isNonEmptyString(errorMessage.value)
})

const show = (urlOrConfirmation: string | Confirmation): void => {
  cancelTitle.value = 'Cancel'
  confirmation.value = null
  confirmationResult.value = null
  isLoadingConfirmation.value = false

  clearError()
  if (typeof urlOrConfirmation == 'string') {
    loadConfirmation(urlOrConfirmation)
  } else {
    confirmation.value = urlOrConfirmation
    if (isNonEmptyString(confirmation.value.title)) {
      modalTitle.value = confirmation.value.title
    }
  }

  modal.value?.show()
}

const hide = (): void => {
  modal.value?.hide()
}

const onModalShown = (): void => {
  emit('modal-shown')
}

const onModalHidden = (): void => {
  emit('modal-hidden')
}

const clearError = (): void => {
  errorMessage.value = ''
  errorDetails.value = null
}

const loadConfirmation = (confirmationUrl: string): void => {
  isLoadingConfirmation.value = true
  const requestHeaders: any = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'X-Requested-With': 'XMLHttpRequest',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'X-CSRF-Token': Rails.csrfToken(),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Content-Type': 'application/vnd.api+json',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Accept': 'application/vnd.api+json'
  }

  axios.get(confirmationUrl, {
    headers: requestHeaders
  })
    .then((response) => {
      const dataFormatter = new Jsona()
      const confirmationObject = dataFormatter.deserialize(response.data)

      if (confirmationObject && !Array.isArray(confirmationObject)
        && confirmationObject.type.type && confirmationObject.type === 'confirmations') {
        confirmation.value = confirmationObject as Confirmation
        if (isNonEmptyString(confirmation.value.title)) {
          modalTitle.value = confirmation.value.title
        }
      } else {
        errorMessage.value = 'Invalid response from server.'
      }
    })
    .catch((error) => {
      errorMessage.value = 'An error occurred'
      if (error && error.message) {
        errorDetails.value = [
          error.message
        ]
      }
      cancelTitle.value = 'Close'
    })
    .then(() => {
      isLoadingConfirmation.value = false
    })
}

const onConfirmAction = (): void => {
  errorMessage.value = ''
  if (confirmation.value?.actionRequest) {
    isPerformingAction.value = true

    let requestParams
    if (typeof confirmation.value.actionRequest.paramsFn === 'function') {
      requestParams = confirmation.value.actionRequest.paramsFn()
    } else {
      requestParams = confirmation.value.actionRequest.params
    }

    axios(confirmation.value.actionRequest.url, {
      method: confirmation.value.actionRequest?.method || 'POST',
      data: requestParams || {},
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Requested-With': 'XMLHttpRequest',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-CSRF-Token': Rails.csrfToken(),
        // 'Content-Type': 'application/vnd.api+json',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Accept': 'application/vnd.api+json'
      }
    })
      .then((response) => {
        const dataFormatter = new Jsona()
        const responseObject = dataFormatter.deserialize(response.data)

        if (responseObject && !Array.isArray(responseObject)
          && responseObject.type && responseObject.type === 'confirmation-results') {
          confirmationResult.value = responseObject as ConfirmationResult
          cancelTitle.value = 'Close'
          if (confirmationResult.value.isSuccess) {
            emit('confirmed')
          }
        } else {
          errorMessage.value = 'Invalid response from server.'
        }
      })
      .catch((error) => {
        errorMessage.value = getAxiosError(error) || ''
      })
      .then(() => {
        isPerformingAction.value = false
      })
  }
}

defineExpose({
  confirmationResult,
  show,
  hide
})

</script>
